import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  buildFactoryOptions,
  buildOutputPerformanceFactoriesFromMetadata
} from './outputPerformanceConfig.js';
import { flattenSource, flattenWeldedCillSource } from './outputPerformanceAdapter.js';
import {
  getAncillariesProduction,
  getCompositeDoorGearing,
  getHardwarePacking,
  getHardwareProduction,
  getNumberOfFrameBoxes,
  getNumberOfPieces,
  getOutputPerformance,
  getOutputPerformanceMetadata,
  getRealdyToLoad,
  getWeldedCillItems,
  getWeldedCillWelding
} from 'services/productionOverviewService';

const AUTO_REFRESH_INTERVAL = 30 * 1000;

const OutputDataContext = createContext(null);

const safeArray = (result) => {
  const records = result?.data?.data || result?.data || [];
  return Array.isArray(records) ? records : [];
};

export function OutputDataProvider({ date, children }) {
  const [rawProduction, setRawProduction] = useState([]);
  const [rawAncillary, setRawAncillary] = useState([]);
  const [rawHardware, setRawHardware] = useState([]);
  const [rawPiecesCut, setRawPiecesCut] = useState([]);
  const [rawFrameBoxes, setRawFrameBoxes] = useState([]);
  const [rawWeldedCill, setRawWeldedCill] = useState([]);
  const [rawCompositeDoorGearing, setRawCompositeDoorGearing] = useState([]);
  const [rawHardwarePacking, setRawHardwarePacking] = useState([]);
  const [rawReadyToLoad, setRawReadyToLoad] = useState([]);
  const [rawWeldedCillWelding, setRawWeldedCillWelding] = useState([]);
  const [metadataFactories, setMetadataFactories] = useState([]);
  const [factoryId, setFactoryId] = useState('');

  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const fetchAllData = useCallback(() => {
    return Promise.all([
      getOutputPerformance(date).then(result => setRawProduction(safeArray(result))).catch(err => console.error('Error fetching output performance items', err)),
      getAncillariesProduction(date).then(result => setRawAncillary(safeArray(result))).catch(err => console.error('Error fetching ancillary items', err)),
      getHardwareProduction(date).then(result => setRawHardware(safeArray(result))).catch(err => console.error('Error fetching hardware items', err)),
      getNumberOfPieces(date).then(result => setRawPiecesCut(safeArray(result))).catch(err => console.error('Error fetching number of pieces', err)),
      getNumberOfFrameBoxes(date).then(result => setRawFrameBoxes(safeArray(result))).catch(err => console.error('Error fetching number of frame boxes', err)),
      getWeldedCillItems(date).then(result => setRawWeldedCill(safeArray(result))).catch(err => console.error('Error fetching welded cill items', err)),
      getCompositeDoorGearing(date).then(result => setRawCompositeDoorGearing(safeArray(result))).catch(err => console.error('Error fetching composite door gearing items', err)),
      getHardwarePacking(date).then(result => setRawHardwarePacking(safeArray(result))).catch(err => console.error('Error fetching hardware packing items', err)),
      getRealdyToLoad(date).then(result => setRawReadyToLoad(safeArray(result))).catch(err => console.error('Error fetching ready to load items', err)),
      getWeldedCillWelding(date).then(result => setRawWeldedCillWelding(safeArray(result))).catch(err => console.error('Error fetching welded cill welding items', err))
    ]);
  }, [date]);

  const loadMetadata = useCallback(() => {
    return getOutputPerformanceMetadata()
      .then(result => {
        const factories = buildOutputPerformanceFactoriesFromMetadata(result?.data);
        setMetadataFactories(factories);
      })
      .catch(err => console.error('Error fetching output performance metadata', err));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadMetadata(), fetchAllData()])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [loadMetadata, fetchAllData]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const isToday = dayjs(date, 'YYYY-MM-DD').isSame(new Date(), 'day');
    if (isToday) {
      timerRef.current = setInterval(() => fetchAllData(), AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [date, fetchAllData]);

  const pools = useMemo(() => ({
    production: [
      ...flattenSource(rawProduction, 'production'),
      ...flattenSource(rawAncillary, 'ancillary'),
      ...flattenSource(rawHardware, 'hardware')
    ],
    piecesCut: flattenSource(rawPiecesCut, 'numberOfPieces'),
    frameBoxes: flattenSource(rawFrameBoxes, 'numberOfFrameBoxes'),
    weldedCill: flattenWeldedCillSource(rawWeldedCill),
    weldedCillWelding: flattenWeldedCillSource(rawWeldedCillWelding),
    compositeDoorGearing: flattenSource(rawCompositeDoorGearing, 'compositeDoorGearing'),
    hardwarePacking: flattenWeldedCillSource(rawHardwarePacking),
    readyToLoad: flattenSource(rawReadyToLoad, 'readlyToLoad')
  }), [rawProduction, rawAncillary, rawHardware, rawPiecesCut, rawFrameBoxes, rawWeldedCill, rawWeldedCillWelding, rawCompositeDoorGearing, rawHardwarePacking, rawReadyToLoad]);

  const factories = Array.isArray(metadataFactories) ? metadataFactories : [];
  const factoryOptions = useMemo(() => buildFactoryOptions(factories), [factories]);

  useEffect(() => {
    if (!factoryOptions.some(option => option.value === factoryId)) {
      setFactoryId(factoryOptions[0]?.value || '');
    }
  }, [factoryId, factoryOptions]);

  const factoryConfig = useMemo(
    () => factories.find(f => f.id === factoryId) || null,
    [factories, factoryId]
  );

  const value = useMemo(() => ({
    pools,
    factories,
    factoryConfig,
    factoryId,
    setFactoryId,
    factoryOptions,
    date,
    loading
  }), [pools, factories, factoryConfig, factoryId, factoryOptions, date, loading]);

  return (
    <OutputDataContext.Provider value={value}>
      {children}
    </OutputDataContext.Provider>
  );
}

OutputDataProvider.propTypes = {
  date: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

export function useOutputData() {
  const ctx = useContext(OutputDataContext);
  if (!ctx) {
    throw new Error('useOutputData must be used within OutputDataProvider');
  }
  return ctx;
}
